import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {injectIntl} from 'react-intl';
import GenericModal from '../../../containers/modal/generic_modal';
import Button from '../../../forecast-app/shared/components/buttons/button/button';
import {BUTTON_STYLE, BUTTON_COLOR, INITIALS_SIZE} from '../../../constants';
import {profilePicSrc} from '../../../constants';
import Util from '../../../forecast-app/shared/util/util';
import CustomScrollDiv from '../../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {getMomentFromCanvasTimelineDate} from '../canvas-timeline/canvas_timeline_util';
import PersonInitials from '../../../forecast-app/shared/components/person/person_initials';
import UtilizationModalEmptyStateGraphic from '../../../images/graphics/utilizationModalEmptyStateGraphic';
import {UtilizationModalStyled, TopSection, StatsSection, DistributionSection, Subtitle, EmptyState} from '../../../styles/v2/schedule_utilization_modal_styled';
import ProjectIdChip from '../../project_id_chip';
import * as tracking from '../../../tracking';
import * as Sentry from '@sentry/browser';
import {CSS_CONSTANTS} from '../../../css_variables';
import {hasFeatureFlag} from '../../../forecast-app/shared/util/FeatureUtil';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';

class UtilizationModal extends Component {
	constructor(props) {
		super(props);

		const {holidayCalendarEntries, holidayCalendars, person, company, startDate, endDate} = props;
		const momentStartDate = getMomentFromCanvasTimelineDate(startDate);
		const momentEndDate = getMomentFromCanvasTimelineDate(endDate);

		const holidayCalendar = person.holidayCalendarId
			? holidayCalendars.find(holidayCalendar => person.holidayCalendarId === holidayCalendar.id)
			: company.defaultHolidayCalendarId
			? holidayCalendars.find(holidayCalendar => company.defaultHolidayCalendarId === holidayCalendar.id)
			: holidayCalendars.length === 1
			? holidayCalendars[0]
			: null;
		this.holidayEntries = [];
		if (holidayCalendar) {
			this.holidayEntries = holidayCalendarEntries.filter(entry => {
				if (entry.holidayCalendarId !== holidayCalendar.id) return false;

				const holidayDate = Util.CreateNonUtcMomentDate(entry.year, entry.month, entry.day);
				return momentStartDate.isSameOrBefore(holidayDate, 'day') && momentEndDate.isSameOrAfter(holidayDate, 'day');
			});
		}
	}

	onAddAllocationClick() {
		const {person, startDate, endDate, projectGroups, projects, idleTimes, projectPersons, actualPersonId, availableFeatureFlags, persons, holidayCalendarEntries, holidayCalendars, company, milestones, roles, teams, teamPersons} = this.props;
		const momentStartDate = getMomentFromCanvasTimelineDate(startDate);
		const momentEndDate = getMomentFromCanvasTimelineDate(endDate);
		const removeLowHigh = hasFeatureFlag('remove_low_high', availableFeatureFlags);

		const commonModalData = {
			projectGroups,
			projects,
			idleTimes,
			projectPersons,
			actualPersonId,
			persons,
			selectedPersonId: person.id,
			allocation: {
				personId: person.id,
				startDay: momentStartDate.date(),
				startMonth: momentStartDate.month() + 1,
				startYear: momentStartDate.year(),
				endDay: momentEndDate.date(),
				endMonth: momentEndDate.month() + 1,
				endYear: momentEndDate.year(),
				monday: person.monday,
				tuesday: person.tuesday,
				wednesday: person.wednesday,
				thursday: person.thursday,
				friday: person.friday,
				saturday: person.saturday,
				sunday: person.sunday,
			},
			removeLowHigh,
			holidayCalendarEntries,
			holidayCalendars,
			company,
		};

		showModal(
			{
				type: MODAL_TYPE.CANVAS_CREATE,
				...commonModalData,
				milestones,
				roles,
				teams,
				teamPersons,
			},
			true
		);

		tracking.trackEvent('Create Allocation from Utilization Modal');
	}

	isAllocationParentDropdownEmpty(projectGroups, projects, idleTimes) {
		if (idleTimes.length) return false;
		let isAllocationParentDropdownEmpty = true;
		for (const projectGroup of projectGroups) {
			if (!projects.find(project => project.projectGroupId === projectGroup.id).useManualAllocations) continue;
			isAllocationParentDropdownEmpty = false;
			break;
		}
		if (!isAllocationParentDropdownEmpty) return false;
		for (const project of projects) {
			if (!project.useManualAllocations) continue;
			isAllocationParentDropdownEmpty = false;
			break;
		}
		return isAllocationParentDropdownEmpty;
	}

	render() {
		const {person, minutesAllocated, minutesAvailable, startDate, endDate, actualPersonId, projects, projectGroups, projectPersons, distributionMap, idleTimes, isPast, company} = this.props;
		const isAllocationParentDropdownEmpty = this.isAllocationParentDropdownEmpty(projectGroups, projects, idleTimes);

		const momentStartDate = getMomentFromCanvasTimelineDate(startDate);
		const momentEndDate = getMomentFromCanvasTimelineDate(endDate);

		const {formatMessage, formatDate} = this.props.intl;

		const shouldShowTimeRegs = isPast && !company.isUsingProjectAllocation && !company.isUsingSchedulingPlanMode;

		const hoursAvailable = minutesAvailable / 60;
		const graphStepCount = 10;
		const graphHeight = 200;
		let graphMaxValue = 10;
		if (minutesAllocated / 60 > graphMaxValue) {
			graphMaxValue = minutesAllocated / 60 < 50 ? 50 : Math.ceil(minutesAllocated / 6000) * 100;
		}
		if (graphMaxValue < hoursAvailable) {
			graphMaxValue = hoursAvailable < 50 ? 50 : Math.ceil(hoursAvailable / 100) * 100;
		}
		const workingHoursLinePosition = (hoursAvailable / graphMaxValue) * graphHeight;

		const graphStepArray = [];
		for (let i = 1; i <= graphStepCount; i++) {
			graphStepArray.push({
				value: Math.round((i / graphStepCount) * graphMaxValue),
				position: (i / graphStepCount) * graphHeight - 8,
				showInfinitySign: !minutesAvailable && i === graphStepCount,
			});
		}

		const distributionArrays = [[], [], [], [], []];
		for (const entry of distributionMap.entries()) {
			if (!entry[1]) continue; //Don't show the row if the allocation is 0 mintues (can be 0h time registration and some other fuckery)
			const decodedId = entry[0] ? atob(entry[0]) : '';
			let color, id, isProjectGroup, name, arrayIndex, isLink;

			if (decodedId.startsWith('ProjectGroup')) {
				const projectGroup = projectGroups.find(projectGroup => {
					return projectGroup.id === entry[0];
				});
				const firstProject = projects.find(project => {
					return project.projectGroupId === projectGroup.id;
				});

				const breadcrumb = {
					message: 'Utilization modal',
					category: 'schedule',
					level: Sentry.Severity.Info,
					data: {
						projectGroup,
						projectGroupId: projectGroup.id,
						projects: projects.map(p => ({id: p.id, projectGroupId: p.projectGroupId})),
					},
				};
				Sentry.addBreadcrumb(breadcrumb);

				id = projectGroup.companyProjectGroupId;
				isProjectGroup = true;
				color = projectGroup.color;
				name = projectGroup.name;
				arrayIndex = firstProject.useManualAllocations ? 1 : 0;
				isLink = hasPermission(PERMISSION_TYPE.PROJECTS_READ_ALL) || projectPersons.filter(projectPerson => projectPerson.projectId === firstProject.id).find(projectPerson => projectPerson.personId === actualPersonId);
			} else if (decodedId.startsWith('ProjectType')) {
				const project = projects.find(project => {
					return project.id === entry[0];
				});
				if (project) {
					id = project.companyProjectId;
					isProjectGroup = false;
					color = project.projectColor;
					name = project.name;
					arrayIndex = project.useManualAllocations ? 1 : 0;
					isLink = hasPermission(PERMISSION_TYPE.PROJECTS_READ_ALL) || projectPersons.filter(projectPerson => projectPerson.projectId === project.id).find(projectPerson => projectPerson.personId === actualPersonId);
				}
			} else if (decodedId.startsWith('IdleTime')) {
				const idleTime = idleTimes.find(idleTime => {
					return idleTime.id === entry[0];
				});
				id = '';
				color = '#efefef';
				name = idleTime.name;
				arrayIndex = idleTime.isInternalTime ? 2 : 3;
				isLink = false;
			} else if (decodedId.startsWith('NoAccess')) {
				id = '';
				color = '#efefef';
				name = formatMessage({id: 'scheduling.no_access'});
				arrayIndex = 4;
				isLink = false;
			}
			if (id != null) {
				distributionArrays[arrayIndex].push({id, isProjectGroup, color, name, minutesAllocated: Util.convertMinutesToFullHour(entry[1], this.props.intl), isLink});
			}
		}
		const daySpan = momentEndDate.diff(momentStartDate, 'days');
		const distributionData = {
			taskAllocation: distributionArrays[0],
			projectAllocation: distributionArrays[1],
			internalTime: distributionArrays[2],
			timeOff: distributionArrays[3],
			otherTime: distributionArrays[4],
		};

		const content = (
			<UtilizationModalStyled>
				<TopSection>
					<div className="person-name-container">
						{person.profilePictureId ? (
							<img crossOrigin="use-credentials" height="32" src={profilePicSrc(person.profilePictureId)} alt="" />
						) : (
							<PersonInitials size={INITIALS_SIZE.LARGE} initials={person.initials ? person.initials : Util.getInitials(`${person.name}`)} />
						)}
						<div className="person-name">
							{person.name} <span className="utilization">{shouldShowTimeRegs ? formatMessage({id: 'common.time_registrations'}) : formatMessage({id: 'scheduling.utilization'})}</span>
						</div>
					</div>
					{!shouldShowTimeRegs && !this.props.readOnly && (hasPermission(PERMISSION_TYPE.ALLOCATION_CREATE) || !company.isUsingProjectAllocation) && !isAllocationParentDropdownEmpty ? (
						<Button
							buttonStyle={BUTTON_STYLE.OUTLINE}
							colorTheme={BUTTON_COLOR.PURPLE}
							text={company.isUsingProjectAllocation ? formatMessage({id: 'scheduling.add_allocation'}) : formatMessage({id: 'public_crete_task_form.title'})}
							onClick={this.onAddAllocationClick.bind(this)}
						/>
					) : null}
				</TopSection>

				<StatsSection>
					<div className="stats-component">
						<Subtitle>
							{company.isUsingProjectAllocation ? formatMessage({id: 'scheduling.total_allocated_h'}) : shouldShowTimeRegs ? formatMessage({id: 'scheduling.total_time_reg_h'}) : formatMessage({id: 'scheduling.total_assigned_h'})}
						</Subtitle>
						<div className="component-value">
							{Util.convertMinutesToFullHour(minutesAllocated, this.props.intl)}
							<span className="available"> / {Util.convertMinutesToFullHour(minutesAvailable, this.props.intl)}</span>
						</div>
					</div>
					{!shouldShowTimeRegs && (
						<>
							<div className="separator"></div>
							<div className="stats-component">
								<Subtitle>{formatMessage({id: 'scheduling.utilization_percent'})}</Subtitle>
								<div className="component-value">
									{minutesAllocated === 0 && minutesAvailable === 0 ? 0 : minutesAvailable ? Math.round((minutesAllocated / minutesAvailable) * 100) : '200'}%{minutesAllocated && !minutesAvailable ? ' +' : ''}
								</div>
							</div>
						</>
					)}
				</StatsSection>

				<DistributionSection>
					{distributionArrays.some(array => array.length) || this.holidayEntries.length ? (
						<>
							<Subtitle>{formatMessage({id: 'common.distribution'})}</Subtitle>
							<div className="data-graph-container">
								<div className="flex-container">
									<CustomScrollDiv autoHeight={true} autoHeightMin={1} autoHeightMax={500}>
										{distributionData.taskAllocation.length ? (
											<div className="utilization-data-section">
												<div className="utilization-data-section-header">
													<div className="utilization-data-section-header-text">{formatMessage({id: 'scheduling.task_allocation'})}</div>
												</div>
												<div className="utilization-data-section-row-container">
													{distributionData.taskAllocation.map(rowData =>
														rowData.isLink ? (
															<a key={rowData.id} href={`/${rowData.isProjectGroup ? 'connected/X-' : 'project/P-'}${rowData.id}`}>
																<div className="utilization-data-section-row" title={rowData.name}>
																	<ProjectIdChip projectColor={rowData.color} isProjectGroup={rowData.isProjectGroup} projectId={rowData.id} />
																	<div className="name">{rowData.name}</div>
																	<div className="minutes-allocated">{rowData.minutesAllocated}</div>
																</div>
															</a>
														) : (
															<div key={rowData.id}>
																<div className="utilization-data-section-row" title={rowData.name}>
																	<ProjectIdChip projectColor={rowData.color} isProjectGroup={rowData.isProjectGroup} projectId={rowData.id} />
																	<div className="name">{rowData.name}</div>
																	<div className="minutes-allocated">{rowData.minutesAllocated}</div>
																</div>
															</div>
														)
													)}
												</div>
											</div>
										) : null}
										{distributionData.projectAllocation.length ? (
											<div className="utilization-data-section" style={{borderTop: distributionData.taskAllocation.length ? 'none' : undefined}}>
												<div className="utilization-data-section-header">
													<div className="utilization-data-section-header-text">{formatMessage({id: 'scheduling.project_allocation'})}</div>
												</div>
												<div className="utilization-data-section-row-container">
													{distributionData.projectAllocation.map(rowData =>
														rowData.isLink ? (
															<a key={rowData.id} href={`/${rowData.isProjectGroup ? 'connected/X-' : 'project/P-'}${rowData.id}`}>
																<div key={rowData.id} className="utilization-data-section-row" title={rowData.name}>
																	<ProjectIdChip projectColor={rowData.color} isProjectGroup={rowData.isProjectGroup} projectId={rowData.id} />
																	<div className="name">{rowData.name}</div>
																	<div className="minutes-allocated">{rowData.minutesAllocated}</div>
																</div>
															</a>
														) : (
															<div key={rowData.id}>
																<div key={rowData.id} className="utilization-data-section-row" title={rowData.name}>
																	<ProjectIdChip projectColor={rowData.color} isProjectGroup={rowData.isProjectGroup} projectId={rowData.id} />
																	<div className="name">{rowData.name}</div>
																	<div className="minutes-allocated">{rowData.minutesAllocated}</div>
																</div>
															</div>
														)
													)}
												</div>
											</div>
										) : null}
										{distributionData.internalTime.length ? (
											<div className="utilization-data-section" style={{borderTop: distributionData.taskAllocation.length || distributionData.projectAllocation.length ? 'none' : undefined}}>
												<div className="utilization-data-section-header">
													<div className="utilization-data-section-header-text">{formatMessage({id: 'common.internal_time'})}</div>
												</div>
												<div className="utilization-data-section-row-container idle">
													{distributionData.internalTime.map((rowData, index) => (
														<div key={'idle-time-' + index} className="utilization-data-section-row" title={rowData.name}>
															<div className="name">{rowData.name}</div>
															<div className="minutes-allocated">{rowData.minutesAllocated}</div>
														</div>
													))}
												</div>
											</div>
										) : null}
										{distributionData.timeOff.length ? (
											<div
												className="utilization-data-section"
												style={{borderTop: distributionData.taskAllocation.length || distributionData.projectAllocation.length || distributionData.internalTime.length ? 'none' : undefined}}
											>
												<div className="utilization-data-section-header">
													<div className="utilization-data-section-header-text">{formatMessage({id: 'common.time_off'})}</div>
												</div>
												<div className="utilization-data-section-row-container time-off">
													{distributionData.timeOff.map((rowData, index) => (
														<div key={'time-off-' + index} className="utilization-data-section-row" title={rowData.name}>
															<div className="name">{rowData.name}</div>
															<div className="minutes-allocated">{rowData.minutesAllocated}</div>
														</div>
													))}
												</div>
											</div>
										) : null}
										{distributionData.otherTime.length ? (
											<div
												className="utilization-data-section"
												style={{borderTop: distributionData.taskAllocation.length || distributionData.projectAllocation.length || distributionData.internalTime.length || distributionData.timeOff.length ? 'none' : undefined}}
											>
												<div className="utilization-data-section-header">
													<div className="utilization-data-section-header-text">{formatMessage({id: 'common.select-other'})}</div>
												</div>
												<div className="utilization-data-section-row-container other-time">
													{distributionData.otherTime.map((rowData, index) => (
														<div key={'other-time-' + index} className="utilization-data-section-row" title={rowData.name}>
															<div className="name">{rowData.name}</div>
															<div className="minutes-allocated">{rowData.minutesAllocated}</div>
														</div>
													))}
												</div>
											</div>
										) : null}
										{this.holidayEntries.length ? (
											<div className="utilization-data-section">
												<div className="utilization-data-section-header">
													<div className="utilization-data-section-header-text">{formatMessage({id: 'common.holidays'})}</div>
												</div>
												<div className="utilization-data-section-row-container time-off">
													{this.holidayEntries.map(entry => {
														const date = Util.CreateNonUtcMomentDate(entry.year, entry.month, entry.day);
														const minutesAllocated = Util.calculateAvilableMinutes(date, date, person.monday, person.tuesday, person.wednesday, person.thursday, person.friday, person.saturday, person.sunday);
														return (
															<div key={'holiday-' + entry.id}>
																<div className="utilization-data-section-row" title={entry.name}>
																	<div className="name">{entry.name}</div>
																	<div className="minutes-allocated">{Util.convertMinutesToFullHour(minutesAllocated, this.props.intl)}</div>
																</div>
															</div>
														);
													})}
												</div>
											</div>
										) : null}
									</CustomScrollDiv>
								</div>

								<div className="graph-section">
									<div className="graph-header">
										{formatDate(momentStartDate, {
											year: 'numeric',
											month: 'numeric',
											day: 'numeric',
										}) +
											(momentStartDate.isSame(momentEndDate, 'day')
												? ''
												: ' - ' +
												  formatDate(momentEndDate, {
														year: 'numeric',
														month: 'numeric',
														day: 'numeric',
												  }))}
									</div>
									<div className="graph">
										<div className="graph-axis" />
										{graphStepArray.map((stepData, index) => (
											<div key={index} className="graph-step" style={{bottom: stepData.position}}>
												{stepData.showInfinitySign ? (
													<div className="infinity-sign-container">
														<div className="infinity-sign" />
													</div>
												) : (
													<div>{stepData.value}</div>
												)}
												<div className="line-container">
													<div className="line" />
												</div>
											</div>
										))}
										<div className="working-hours-line" style={{bottom: workingHoursLinePosition}} />
										<div className="graph-grey-block" style={{height: workingHoursLinePosition}} />
										<div
											className="graph-green-block"
											style={{
												height: minutesAllocated > minutesAvailable ? workingHoursLinePosition : (minutesAllocated / 60 / graphMaxValue) * graphHeight,
												backgroundColor: shouldShowTimeRegs ? CSS_CONSTANTS.schedule_actual_blue : CSS_CONSTANTS.schedule_v2_green,
											}}
										/>
										{minutesAllocated > minutesAvailable ? (
											<div
												className="graph-red-block"
												style={{
													bottom: workingHoursLinePosition,
													height: minutesAvailable ? ((minutesAllocated - minutesAvailable) / 60 / graphMaxValue) * graphHeight : graphHeight,
													backgroundColor: shouldShowTimeRegs ? CSS_CONSTANTS.schedule_actual_blue : CSS_CONSTANTS.schedule_v2_red,
												}}
											/>
										) : null}
										{minutesAvailable && minutesAllocated !== minutesAvailable ? (
											<div
												className="difference-text"
												style={{
													bottom: minutesAvailable > minutesAllocated ? workingHoursLinePosition : workingHoursLinePosition + ((minutesAllocated - minutesAvailable) / 60 / graphMaxValue) * graphHeight,
												}}
											>
												{minutesAvailable > minutesAllocated ? Math.round((1 - 1 / (minutesAvailable / minutesAllocated)) * 100) : Math.round((minutesAllocated / minutesAvailable - 1) * 100)}%{' '}
												<span>{minutesAvailable > minutesAllocated ? formatMessage({id: 'scheduling.under'}) : formatMessage({id: 'scheduling.over'})}</span>
											</div>
										) : null}
										<div className="graph-footer">{daySpan > 10 ? formatMessage({id: 'scheduling.hours_month'}) : daySpan < 4 ? formatMessage({id: 'scheduling.hours_day'}) : formatMessage({id: 'scheduling.hours_week'})}</div>
									</div>
								</div>
							</div>
						</>
					) : (
						<EmptyState>
							<UtilizationModalEmptyStateGraphic />
							<span className="message">
								{formatMessage(
									{id: company.isUsingProjectAllocation ? 'scheduling.person_has_nothing_allocated' : shouldShowTimeRegs ? 'scheduling.person_has_no_time_reg' : 'scheduling.person_has_nothing_assigned'},
									{personName: person.name}
								)}
							</span>
							{!shouldShowTimeRegs && !this.props.readOnly && (hasPermission(PERMISSION_TYPE.ALLOCATION_CREATE) || !company.isUsingProjectAllocation) && !isAllocationParentDropdownEmpty ? (
								<span>{company.isUsingProjectAllocation ? formatMessage({id: 'scheduling.add_allocations_using_button'}) : formatMessage({id: 'scheduling.add_tasks_using_button'})}</span>
							) : null}
						</EmptyState>
					)}
				</DistributionSection>
			</UtilizationModalStyled>
		);
		return <GenericModal closeModal={this.props.closeModal} content={content} className="utilization-modal" userPilot={'utilization-modal'} />;
	}
}

UtilizationModal.propTypes = {
	person: PropTypes.object.isRequired,
	company: PropTypes.object.isRequired,
	startDate: PropTypes.number.isRequired,
	endDate: PropTypes.number.isRequired,
	minutesAllocated: PropTypes.number.isRequired,
	minutesAvailable: PropTypes.number.isRequired,
	distributionMap: PropTypes.object.isRequired,
	actualPersonId: PropTypes.string.isRequired,
	projects: PropTypes.array.isRequired,
	projectGroups: PropTypes.array.isRequired,
	projectPersons: PropTypes.array.isRequired,
	idleTimes: PropTypes.array.isRequired,
	persons: PropTypes.array,
	readOnly: PropTypes.bool.isRequired,
};

UtilizationModal.defaultPropTypes = {
	readOnly: false,
};

export default injectIntl(UtilizationModal);
